import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Group,
  Loader,
  Alert,
  Table,
  Text,
  Paper,
  ActionIcon,
  Tooltip,
  Button,
  CopyButton,
  Stack,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconSettings,
  IconExternalLink,
  IconCopy,
  IconCheck,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";

// Format an ISO timestamp for display, leaving empty values blank.
function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

// Render a plain value, leaving empty values blank.
function displayValue(value) {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "";
  return value;
}

function WelcomeSurveyAdminPage() {
  const { getVerifiedJWT } = useAuth();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewingSurveyId, setViewingSurveyId] = useState(null);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint("/v1/welcome-survey/report"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) throw new Error("Failed to load welcome survey report");

      const result = await response.json();
      // The endpoint returns the survey list either as the top-level body or
      // wrapped under `data`.
      const data = Array.isArray(result) ? result : result?.data;
      const rows = Array.isArray(data)
        ? data
        : (data?.surveys ?? data?.reports ?? data?.rows ?? []);
      setSurveys(rows);
    } catch (err) {
      setError(err.message || "Failed to load welcome survey report");
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Fetch the signed document URL for a completed survey and open it.
  const handleViewSurvey = useCallback(
    async surveyId => {
      if (!surveyId) return;
      setViewingSurveyId(surveyId);
      setError(null);
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) throw new Error("Authentication failed");

        const response = await fetch(
          getApiEndpoint(
            `/v1/welcome-survey/document/${encodeURIComponent(surveyId)}`
          ),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (!response.ok)
          throw new Error("Failed to load welcome survey document");

        const result = await response.json();
        const data = result?.data ?? result;
        const url = data?.url ?? data?.document_url;

        if (!url) {
          setError("No survey document was found for this survey.");
          return;
        }

        window.open(url, "_blank", "noopener,noreferrer");
      } catch (err) {
        setError(err.message || "Failed to load welcome survey document");
      } finally {
        setViewingSurveyId(null);
      }
    },
    [getVerifiedJWT]
  );

  const renderBody = () => {
    if (loading) {
      return (
        <Group justify="center" py="xl">
          <Loader size="lg" />
        </Group>
      );
    }

    if (error) {
      return (
        <Alert
          icon={<IconAlertCircle size={20} />}
          title="Error"
          color="red"
          variant="filled"
        >
          {error}
        </Alert>
      );
    }

    if (!surveys.length) {
      return (
        <Text c="dimmed" py="xl" ta="center">
          No welcome surveys to show yet.
        </Text>
      );
    }

    return (
      <Paper radius="md" withBorder bg="white" style={{ overflow: "hidden" }}>
        <Table.ScrollContainer minWidth={1550}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Customer Id</Table.Th>
                <Table.Th>Office</Table.Th>
                <Table.Th>Survey</Table.Th>
                <Table.Th>Rep Phone Number</Table.Th>
                <Table.Th>Sent / Completed</Table.Th>
                <Table.Th>Survey Sent To</Table.Th>
                <Table.Th>Subscription Ids</Table.Th>
                <Table.Th>Survey Completed By</Table.Th>
                <Table.Th>Invalidated At</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {surveys.map((survey, index) => (
                <Table.Tr key={survey.id ?? index}>
                  <Table.Td>{displayValue(survey.fr_customer_id)}</Table.Td>
                  <Table.Td>
                    <Stack gap={2} align="flex-start">
                      <Text size="sm">
                        {displayValue(survey.fieldroutes_office?.name)}
                      </Text>
                      {survey.fieldroutes_office?.fr_office_id != null && (
                        <Text size="xs" c="dimmed">
                          Office ID: {survey.fieldroutes_office.fr_office_id}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap="xs" align="flex-start">
                      <Group gap="xs" wrap="nowrap">
                        <Text size="xs" c="dimmed">
                          #{displayValue(survey.id)}
                        </Text>
                        <Group gap={2} wrap="nowrap">
                          {survey.survey_code && (
                            <Text size="xs" c="dimmed">
                              {survey.survey_code}
                            </Text>
                          )}
                          {survey.survey_url && (
                            <CopyButton
                              value={survey.survey_url}
                              timeout={1500}
                            >
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={copied ? "Copied" : "Copy survey link"}
                                  withArrow
                                >
                                  <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    size="lg"
                                    aria-label="Copy survey link"
                                    onClick={copy}
                                  >
                                    {copied ? (
                                      <IconCheck size={16} />
                                    ) : (
                                      <IconCopy size={16} />
                                    )}
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </CopyButton>
                          )}
                        </Group>
                      </Group>
                    </Stack>
                  </Table.Td>
                  <Table.Td>{displayValue(survey.rep_phone)}</Table.Td>
                  <Table.Td>
                    <Stack gap="xs" align="flex-start">
                      {survey.survey_sent_at && (
                        <Text size="xs" style={{ whiteSpace: "nowrap" }}>
                          Sent at:{" "}
                          <Text span c="dimmed" size="xs">
                            {formatDateTime(survey.survey_sent_at)}
                          </Text>
                        </Text>
                      )}
                      {survey.completed_at && (
                        <>
                          <Text size="xs" style={{ whiteSpace: "nowrap" }}>
                            Completed at:{" "}
                            <Text span c="dimmed" size="xs">
                              {formatDateTime(survey.completed_at)}
                            </Text>
                          </Text>
                          {survey.upload_id && (
                            <Button
                              variant="filled"
                              radius={6}
                              leftSection={<IconExternalLink size={14} />}
                              loading={viewingSurveyId === survey.id}
                              disabled={viewingSurveyId !== null}
                              onClick={() => handleViewSurvey(survey.id)}
                              styles={{
                                root: {
                                  height: 30,
                                  padding: "6px 12px",
                                  fontSize: 13,
                                  fontWeight: 500,
                                },
                                section: { marginRight: 5 },
                              }}
                            >
                              View Completed Survey
                            </Button>
                          )}
                        </>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    {displayValue(survey.sent_phone || survey.sent_email)}
                  </Table.Td>
                  <Table.Td>{displayValue(survey.subscription_ids)}</Table.Td>
                  <Table.Td>
                    <Stack gap="xs" align="flex-start">
                      {survey.ip_address && (
                        <Text size="xs" style={{ whiteSpace: "nowrap" }}>
                          IP Address:{" "}
                          <Text span c="dimmed" size="xs">
                            {displayValue(survey.ip_address)}
                          </Text>
                        </Text>
                      )}
                      {survey.device_id && (
                        <Text size="xs" style={{ whiteSpace: "nowrap" }}>
                          Device Id:{" "}
                          <Text span c="dimmed" size="xs">
                            {displayValue(survey.device_id)}
                          </Text>
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>{formatDateTime(survey.invalidated_at)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    );
  };

  return (
    <PageContent
      title="Welcome Survey"
      subtitle="Send and manage customer welcome surveys."
      headerActions={
        <Tooltip label="Settings">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            aria-label="Welcome survey settings"
            onClick={() => navigate("/welcome-survey/settings")}
          >
            <IconSettings size={22} />
          </ActionIcon>
        </Tooltip>
      }
    >
      {renderBody()}
    </PageContent>
  );
}

export default WelcomeSurveyAdminPage;
